<script setup lang="ts">
import { computed } from "vue";

import { teamSelected } from "@/analytics/events/search";
import { trackEvent } from "@/analytics/track";
import SelectableList from "@/components/ui/SelectableList/SelectableList.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import type { Team } from "@/model/session";
import { useSearchMenuStore } from "@/store/search/searchMenu";

import TeamItemTooltip from "../tooltips/TeamItemTooltip.vue";
import SearchDropdown from "./SearchDropdown.vue";

const store = useSearchMenuStore();

// Only reserve the icon column while external orgs are present in the list, so
// the regular team list stays flush left when there are none.
const hasExternalOrgs = computed(() => store.allTeams.some((team) => team.external));

const selection = computed({
  get: () => store.selectedTeams,
  set: (values: Team[]) => {
    const added = values.filter((team) => !store.selectedTeamIds.has(team.id));
    store.selectedTeamIds = new Set(values.map((val) => val.id));
    added.forEach((team) => trackEvent(teamSelected(!!team.external, hasExternalOrgs.value)));
  },
});
</script>

<template>
  <SearchDropdown
    name="team"
    icon="persona/team"
    label-key="general.teams"
    :selected="selection.length > 0"
  >
    <template #tooltip>
      <TeamItemTooltip :teams="selection" />
    </template>
    <SelectableList
      v-model="selection"
      v-keyboard-nav
      :values="store.allTeams"
      :header="$t('general.teams')"
    >
      <template #default="{ item }">
        <span v-if="hasExternalOrgs" class="team-option-icon">
          <SvgIcon v-if="item.external" name="persona/external-team" width="16" height="16" />
        </span>
        {{ item.name }}
      </template>
    </SelectableList>
  </SearchDropdown>
</template>

<style lang="scss" scoped>
// Inline (not flex) so the parent's text-overflow ellipsis applies to the name
.team-option-icon {
  display: inline-flex;
  vertical-align: middle;
  width: 16px;
  height: 16px;
  margin-right: 8px;
}
</style>
